<script setup lang="ts">
// ================================
// « Résumé pour votre client » (contrat de l'interface v1.2 §7.5 ; maquette
// 2c ; D16 a ; DEV-2311, lot 4b).
//
// Ce que le courtier doit vivre : un texte prêt à coller dans un courriel à
// son client, lu AVANT d'être copié : le verdict en gras, en mots et sans
// signe, la phrase d'ouverture, les puces « Pourquoi » (la raison de la carte,
// mot pour mot), « À demander au fournisseur » (sans code) et « Avant
// d'envoyer », puis la ligne de périmètre. « Copier le résumé » le copie en
// texte enrichi et en texte brut.
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { NIVEAU_2 } from "@modules/courtage/utils/optim-turpe-v2.boutons"
import {
    texteDuResume,
    type ResumeDeLaCarte,
} from "@modules/courtage/utils/optim-turpe-v2.carte"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            copier: "Copier le résumé",
            copie: "Résumé copié",
            copieImpossible:
                "La copie n'a pas abouti. Sélectionnez le résumé pour le reprendre.",
        },
    },
})

const props = defineProps<{ resume: ResumeDeLaCarte }>()

const copie = ref<"ok" | "echec" | null>(null)
watch(
    () => props.resume,
    () => {
        copie.value = null
    }
)

/** Le texte brut copié (puces à tiret), espaces insécables simplifiées. */
const getTexte = computed(() =>
    texteDuResume(props.resume).replace(/[\u00a0\u202f]/g, " ")
)

/** Le même résumé en HTML (titre en gras, puces), pour les messageries. */
function enHtml(resume: ResumeDeLaCarte): string {
    const echapper = (v: string) =>
        v
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/[\u00a0\u202f]/g, " ")
    return [
        `<p><strong>${echapper(resume.verdict)}</strong></p>`,
        resume.ouverture ? `<p>${echapper(resume.ouverture)}</p>` : "",
        resume.puces.length > 0
            ? `<ul>${resume.puces.map((p) => `<li>${echapper(p)}</li>`).join("")}</ul>`
            : "",
        `<p>${echapper(resume.perimetre)}</p>`,
    ].join("")
}

async function onCopier(): Promise<void> {
    try {
        const Item = (globalThis as { ClipboardItem?: typeof ClipboardItem })
            .ClipboardItem
        if (Item && typeof navigator.clipboard?.write === "function") {
            await navigator.clipboard.write([
                new Item({
                    "text/plain": new Blob([getTexte.value], {
                        type: "text/plain",
                    }),
                    "text/html": new Blob([enHtml(props.resume)], {
                        type: "text/html",
                    }),
                }),
            ])
        } else {
            await navigator.clipboard.writeText(getTexte.value)
        }
        copie.value = "ok"
    } catch {
        copie.value = "echec"
    }
}
</script>

<template>
    <div class="flex flex-col gap-3" data-testid="optim-turpe-v2-remise">
        <div
            class="flex flex-col gap-2 border-l-2 border-surface-200 pl-3 text-sm text-primary-800"
            data-testid="optim-turpe-v2-resume"
        >
            <p
                class="font-semibold"
                data-testid="optim-turpe-v2-resume-verdict"
            >
                {{ resume.verdict }}
            </p>
            <p v-if="resume.ouverture">{{ resume.ouverture }}</p>
            <ul v-if="resume.puces.length > 0" class="list-disc pl-5">
                <li
                    v-for="puce in resume.puces"
                    :key="puce"
                    :data-codes-postes="
                        puce.startsWith('À demander') ? '' : undefined
                    "
                    data-testid="optim-turpe-v2-resume-puce"
                >
                    {{ puce }}
                </li>
            </ul>
            <p class="text-xs text-surface-500">{{ resume.perimetre }}</p>
        </div>
        <div class="flex flex-col items-start gap-1">
            <BaseButton
                v-bind="NIVEAU_2"
                :label="t('copier')"
                icon="pi pi-copy"
                class="w-fit"
                data-testid="optim-turpe-v2-copier-resume"
                @click="onCopier"
            />
            <p
                class="min-h-4 text-xs break-words whitespace-normal"
                :class="
                    copie === 'echec'
                        ? 'text-[var(--p-orange-700)]'
                        : 'text-surface-500'
                "
                role="status"
                data-testid="optim-turpe-v2-copie-resume-statut"
            >
                {{
                    copie === "ok"
                        ? t("copie")
                        : copie === "echec"
                          ? t("copieImpossible")
                          : ""
                }}
            </p>
        </div>
    </div>
</template>
